import { useMemo } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { ROLES } from '@/lib/constants/app.constants';

export interface UserPermissions {
  isSystemAdmin: boolean;
  isBookingRequester: boolean;
  /** True when the active user is a booking-requester without elevated (system admin) privileges. */
  hideRestrictedFilters: boolean;
  hasRole: (roleId: string) => boolean;
}

/**
 * Normalizes role IDs to lowercase so comparisons are resilient to DB/provider casing.
 */
function normalizeRoles(roles: readonly string[] | undefined): string[] {
  if (!roles || roles.length === 0) {
    return [];
  }
  return roles.map((r) => r.toLowerCase());
}

/**
 * Centralizes role-based UI gating. Keeps comparisons case-insensitive and avoids
 * duplicating `roles.includes(...)` in every page / component.
 */
export function useUserPermissions(): UserPermissions {
  const { user } = useAuth();

  return useMemo(() => {
    const normalized = normalizeRoles(user?.roles);
    const hasRole = (roleId: string): boolean => normalized.includes(roleId.toLowerCase());
    const isSystemAdmin = hasRole(ROLES.SYSTEM_ADMIN);
    const isBookingRequester = hasRole(ROLES.BOOKING_REQUESTER);
    return {
      isSystemAdmin,
      isBookingRequester,
      hideRestrictedFilters: isBookingRequester && !isSystemAdmin,
      hasRole,
    };
  }, [user?.roles]);
}
